// src/pages/sysadmin/SysAdminDashboard.jsx
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  Server, Database, Shield, Activity, Cpu, HardDrive,
  RefreshCw, AlertTriangle, CheckCircle, XCircle,
  Clock, Eye, Ban, Trash2, Users, Terminal
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const SysAdminDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // États des données
  const [failedLogins, setFailedLogins] = useState([]);
  const [bruteForceIPs, setBruteForceIPs] = useState([]);
  const [blacklistedIPs, setBlacklistedIPs] = useState([]);
  const [dbStats, setDbStats] = useState(null);
  const [healthChecks, setHealthChecks] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [blockingIP, setBlockingIP] = useState(null);

  // Fetch toutes les données
  const fetchAll = useCallback(async () => {
    setRefreshing(true);
    try {
      const [
        failedRes,
        bruteRes,
        blacklistRes,
        dbRes,
        healthRes,
        auditRes
      ] = await Promise.all([
        axios.get(`${API_BASE}/sysadmin/security/failed-logins?hours=24&limit=50`),
        axios.get(`${API_BASE}/sysadmin/security/brute-force-ips?threshold=5`),
        axios.get(`${API_BASE}/sysadmin/security/ip-blacklist`),
        axios.get(`${API_BASE}/sysadmin/database/stats`),
        axios.get(`${API_BASE}/sysadmin/health`),  // ← CORRIGÉ : /health au lieu de /health/all
        axios.get(`${API_BASE}/sysadmin/audit-logs?limit=50`)
      ]);

      setFailedLogins(failedRes.data?.failed_logins || []);
      setBruteForceIPs(bruteRes.data?.suspicious_ips || []);
      setBlacklistedIPs(blacklistRes.data?.blacklisted_ips || []);
      setDbStats(dbRes.data);
      setHealthChecks(healthRes.data?.services || []);
      setAuditLogs(auditRes.data?.audit_logs || []);
    } catch (error) {
      console.error('Error fetching sysadmin data:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    const interval = setInterval(fetchAll, 60000); // Refresh chaque minute
    return () => clearInterval(interval);
  }, [fetchAll]);

  // Bloquer une IP
  const handleBlockIP = async (ipAddress) => {
    if (!window.confirm(`Bloquer l'IP ${ipAddress} ?`)) return;
    setBlockingIP(ipAddress);
    try {
      await axios.post(`${API_BASE}/sysadmin/security/ip-blacklist`, {
        ip_address: ipAddress,
        reason: 'Tentatives de connexion suspectes',
        hours: 24
      });
      await fetchAll();
    } catch (error) {
      console.error('Error blocking IP:', error);
      alert('Erreur lors du blocage de l\'IP');
    } finally {
      setBlockingIP(null);
    }
  };

  // Débloquer une IP
  const handleUnblockIP = async (ipAddress) => {
    if (!window.confirm(`Débloquer l'IP ${ipAddress} ?`)) return;
    try {
      await axios.delete(`${API_BASE}/sysadmin/security/ip-blacklist/${ipAddress}`);
      await fetchAll();
    } catch (error) {
      console.error('Error unblocking IP:', error);
      alert('Erreur lors du déblocage de l\'IP');
    }
  };

  // Composant KPI Card
  const StatCard = ({ title, value, icon: Icon, color, subtext }) => (
    <div className="bg-gray-800/60 rounded-xl border border-gray-700 p-4">
      <div className="flex items-center justify-between mb-2">
        <p className="text-gray-400 text-sm">{title}</p>
        <div className={`p-2 rounded-lg ${color}`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <p className="text-2xl font-bold text-white">{value}</p>
      {subtext && <p className="text-xs text-gray-500 mt-1">{subtext}</p>}
    </div>
  );

  // Statut badge
  const StatusBadge = ({ status }) => {
    if (status === 'up') {
      return <span className="flex items-center gap-1 text-green-400"><CheckCircle className="w-3 h-3" /> UP</span>;
    }
    if (status === 'down') {
      return <span className="flex items-center gap-1 text-red-400"><XCircle className="w-3 h-3" /> DOWN</span>;
    }
    return <span className="flex items-center gap-1 text-yellow-400"><AlertTriangle className="w-3 h-3" /> DEGRADED</span>;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Terminal className="w-6 h-6 text-purple-400" />
            Infrastructure Dashboard
          </h1>
          <p className="text-gray-400 text-sm">Monitoring système, sécurité et audit</p>
        </div>
        <button
          onClick={fetchAll}
          disabled={refreshing}
          className="p-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-400 hover:text-white transition"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard
          title="Connexions DB"
          value={dbStats?.connections || 0}
          icon={Database}
          color="bg-blue-500/20 text-blue-400"
          subtext={`Taille: ${dbStats?.database_size || '0 GB'}`}
        />
        <StatCard
          title="Cache Hit Ratio"
          value={`${dbStats?.cache_hit_ratio || 0}%`}
          icon={HardDrive}
          color="bg-green-500/20 text-green-400"
          subtext="PostgreSQL cache"
        />
        <StatCard
          title="Requêtes lentes"
          value={dbStats?.slow_queries_24h || 0}
          icon={Clock}
          color="bg-yellow-500/20 text-yellow-400"
          subtext="24 dernières heures"
        />
        <StatCard
          title="Échecs de connexion"
          value={failedLogins.length}
          icon={Shield}
          color="bg-red-500/20 text-red-400"
          subtext="24 dernières heures"
        />
      </div>

      {/* Service Health */}
      <div className="bg-gray-800/60 rounded-xl border border-gray-700 p-4">
        <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
          <Activity className="w-4 h-4 text-green-400" />
          État des services
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {healthChecks.map((service) => (
            <div key={service.service_name} className="bg-gray-900/50 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <span className="text-gray-300 text-sm capitalize">{service.service_name}</span>
                <StatusBadge status={service.status} />
              </div>
              {service.response_time_ms && (
                <p className="text-xs text-gray-500 mt-1">{service.response_time_ms}ms</p>
              )}
              {service.error_message && (
                <p className="text-xs text-red-400 mt-1 truncate">{service.error_message}</p>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Suspicious IPs (Brute Force) */}
      {bruteForceIPs.length > 0 && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
          <h3 className="text-red-400 font-semibold mb-3 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            IPs suspectes (Brute Force)
          </h3>
          <div className="space-y-2">
            {bruteForceIPs.map((ip) => (
              <div key={ip.ip_address} className="flex flex-col sm:flex-row sm:items-center justify-between bg-gray-900/50 rounded-lg p-3 gap-3">
                <div className="flex-1">
                  <code className="text-red-400 text-sm">{ip.ip_address}</code>
                  <p className="text-xs text-gray-500">{ip.attempts} tentatives</p>
                  {ip.attempted_emails?.length > 0 && (
                    <p className="text-xs text-gray-500">Emails ciblés: {ip.attempted_emails.join(', ')}</p>
                  )}
                </div>
                {!blacklistedIPs.some(b => b.ip_address === ip.ip_address) && (
                  <button
                    onClick={() => handleBlockIP(ip.ip_address)}
                    disabled={blockingIP === ip.ip_address}
                    className="px-3 py-1.5 bg-red-500/20 hover:bg-red-500/30 rounded-lg text-red-400 text-sm transition whitespace-nowrap"
                  >
                    {blockingIP === ip.ip_address ? 'Blocage...' : 'Bloquer IP'}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Blacklisted IPs */}
      {blacklistedIPs.length > 0 && (
        <div className="bg-gray-800/60 rounded-xl border border-gray-700 p-4">
          <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
            <Ban className="w-4 h-4 text-red-400" />
            IPs blacklistées
          </h3>
          <div className="space-y-2">
            {blacklistedIPs.map((ip) => (
              <div key={ip.ip_address} className="flex flex-col sm:flex-row sm:items-center justify-between bg-gray-900/50 rounded-lg p-3 gap-3">
                <div className="flex-1">
                  <code className="text-gray-300 text-sm">{ip.ip_address}</code>
                  <p className="text-xs text-gray-500">{ip.reason || 'Aucune raison'}</p>
                  {ip.expires_at && (
                    <p className="text-xs text-gray-500">Expire le: {new Date(ip.expires_at).toLocaleString()}</p>
                  )}
                </div>
                <button
                  onClick={() => handleUnblockIP(ip.ip_address)}
                  className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-sm transition whitespace-nowrap"
                >
                  Débloquer
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Failed Logins Table */}
      <div className="bg-gray-800/60 rounded-xl border border-gray-700 overflow-hidden">
        <div className="p-4 border-b border-gray-700">
          <h3 className="text-white font-semibold flex items-center gap-2">
            <Shield className="w-4 h-4 text-red-400" />
            Tentatives de connexion échouées
          </h3>
        </div>
        <div className="overflow-x-auto max-h-96">
          <table className="w-full text-sm">
            <thead className="bg-gray-800 sticky top-0">
              <tr className="text-gray-400 border-b border-gray-700">
                <th className="p-3 text-left">IP Address</th>
                <th className="p-3 text-left">Email</th>
                <th className="p-3 text-left">Type</th>
                <th className="p-3 text-left">Endpoint</th>
                <th className="p-3 text-left">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700/50">
              {failedLogins.map((log, idx) => (
                <tr key={idx} className="hover:bg-gray-700/30">
                  <td className="p-3 font-mono text-xs text-gray-300">{log.ip_address}</td>
                  <td className="p-3 text-gray-400">{log.user_email || '-'}</td>
                  <td className="p-3">
                    <span className={`px-2 py-0.5 rounded text-xs ${
                      log.user_type === 'staff' ? 'bg-blue-500/20 text-blue-400' : 'bg-gray-500/20 text-gray-400'
                    }`}>
                      {log.user_type || 'unknown'}
                    </span>
                  </td>
                  <td className="p-3 text-gray-500 text-xs">{log.endpoint || '-'}</td>
                  <td className="p-3 text-gray-500 text-xs">
                    {new Date(log.created_at).toLocaleString()}
                  </td>
                </tr>
              ))}
              {failedLogins.length === 0 && (
                <tr>
                  <td colSpan="5" className="p-8 text-center text-gray-500">
                    Aucune tentative échouée
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Audit Logs Table */}
      <div className="bg-gray-800/60 rounded-xl border border-gray-700 overflow-hidden">
        <div className="p-4 border-b border-gray-700">
          <h3 className="text-white font-semibold flex items-center gap-2">
            <Eye className="w-4 h-4 text-purple-400" />
            Journal d'audit admin
          </h3>
        </div>
        <div className="overflow-x-auto max-h-96">
          <table className="w-full text-sm">
            <thead className="bg-gray-800 sticky top-0">
              <tr className="text-gray-400 border-b border-gray-700">
                <th className="p-3 text-left">Admin</th>
                <th className="p-3 text-left">Action</th>
                <th className="p-3 text-left">Cible</th>
                <th className="p-3 text-left">IP</th>
                <th className="p-3 text-left">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700/50">
              {auditLogs.map((log, idx) => (
                <tr key={idx} className="hover:bg-gray-700/30">
                  <td className="p-3 text-gray-300">{log.staff_email || 'Système'}</td>
                  <td className="p-3">
                    <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 rounded text-xs">
                      {log.action}
                    </span>
                  </td>
                  <td className="p-3 text-gray-400 text-xs">
                    {log.target_type} {log.target_id?.slice(0, 8)}...
                  </td>
                  <td className="p-3 font-mono text-xs text-gray-500">{log.ip_address || '-'}</td>
                  <td className="p-3 text-gray-500 text-xs">
                    {new Date(log.created_at).toLocaleString()}
                  </td>
                </tr>
              ))}
              {auditLogs.length === 0 && (
                <tr>
                  <td colSpan="5" className="p-8 text-center text-gray-500">
                    Aucun log d'audit
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SysAdminDashboard;